import { useCallback, useEffect, useState } from 'react';

interface useScriptsReturn {
  loading: boolean;
  error: string | undefined;
  value: Event | undefined;
}
/**
 * # useScripts

Хук `useScripts` предназначен для асинхронной загрузки скриптов по массиву URL-адресов. Он возвращает массив объектов `Result`, содержащих информацию о состоянии загрузки каждого скрипта.

## Параметры

- `urls`: Массив строк, представляющих URL-адреса скриптов для загрузки.

## Возвращаемое значение

Массив объектов `Result`, где каждый объект содержит следующие поля:
- `loading`: Логическое значение, указывающее на состояние загрузки скрипта.
- `error`: Строка с описанием ошибки, если при загрузке скрипта произошла ошибка.
- `value`: Результат загрузки скрипта.

 * @example
 * const results = useScripts(['https://example.com/script1.js', 'https://example.com/script2.js']);
 *
 */

export const useScripts = (urls: string[]): useScriptsReturn[] => {
  const [results, setResults] = useState<useScriptsReturn[]>(
    urls.map(() => ({
      loading: true,
      error: undefined,
      value: undefined
    }))
  );

  const fetchUrl = useCallback((url: string, index: number) => {
    return new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = url;
      script.async = true;

      const handleLoad = (event: Event) => {
        setResults((prev) =>
          prev.map((result, i) =>
            i === index
              ? {
                  loading: false,
                  error: undefined,
                  value: event
                }
              : result
          )
        );
        resolve();
      };

      const handleError = (event: Event) => {
        setResults((prev) =>
          prev.map((result, i) =>
            i === index
              ? {
                  loading: false,
                  error: `Error loading script from ${url}`,
                  value: event
                }
              : result
          )
        );
        reject();
      };

      script.addEventListener('load', handleLoad);
      script.addEventListener('error', handleError);

      document.body.appendChild(script);
    });
  }, []);

  useEffect(() => {
    urls.forEach((url, index) => {
      fetchUrl(url, index);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...urls]);

  return results;
};
